import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m here to help with questions about our tech support services. What can I help you with?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  //how gemini gets api key
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const genAI = new GoogleGenerativeAI(apiKey);
  const model = genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });

  //context for the chatbot
  const systemContext = `You are the customer service assistant for MTS Tech Solutions (also called Mobile Tech Solutions / MTS), a mobile in-home tech support company. We come to the customer's home — we do not ship devices or operate a storefront.

These are the services we offer.(these are the ONLY services we offer and their categories, do not invent or deny others beyond this list):

INSTALL & SETUP (infrastructure and physical installs):
1. Cable Management — Organizing and hiding tangled cables behind desks, TVs, and entertainment centers. Routing, labeling, concealing with raceways/sleeves, and making setups look clean and intentional. Common materials: cable raceway kits, velcro ties, cable sleeves, under-desk trays, adhesive clips.
2. Network Setup — WiFi coverage, wired (Ethernet) installs, router/mesh setup, home network configuration. Common materials: Cat6 cables, wall plates, surge protectors.
3. Tech and TV Mounting — Wall-mounting TVs and installing new devices cleanly. Handles the install, cable hiding, positioning, and connections. Common materials: TV wall mount brackets, cable hider kits, HDMI cables, surge-protected extension cords.
4. System Setup — Setting up new computers, phones, tablets, or other devices. Data migration, account setup, walkthroughs.

DATA PROTECTION:
5. Cloud or Disk Backup — Setting up automatic cloud backups (Google Drive, OneDrive, iCloud, Backblaze) or local disk backups to external hard drives. Includes scheduling, verifying backups work, and recovering lost files when needed. Common materials: external hard drives (1TB/2TB), USB flash drives, cloud backup subscriptions.

ONGOING SUPPORT:
6. Tech Support — General tech help and consultation when you just need a human who speaks both tech and plain English.

Key info:
- Service area: We serve a 12-mile radius around Carson, CA. This covers the South Bay and surrounding communities including Long Beach, Torrance, Compton, Lakewood, Inglewood, Downey, Hawthorne, Wilmington, Gardena, and Paramount — plus other nearby cities within range. All service is in-home (we come to you). If someone asks whether we cover their area, check if it's roughly within 12 miles of Carson, CA. Encourage them to submit a request form and we will contact them to discuss it further, even if they are just outside the radius. Even if outside the radius, we may still be able to accommodate them depending on the specific location and circumstances. encurage them to submit a request form and we will contact them to discuss it further.
- Contact: jorge@mtstechsolutions.com
- Hours: Mon–Thu 9am–7pm, Fri 9am–5pm, Sat 10am–3pm, Sunday closed
- Same-day response to requests
- Flat-rate pricing, no hidden fees, free assessments

Rules:
- If asked about a service we offer, confirm it enthusiastically and briefly describe what's involved. Yes, we DO offer TV mounting/installation — it's service #3.
- If asked about something NOT on the list (e.g., phone screen repair, commercial IT contracts, device sales), politely say it's not something we currently offer and suggest they submit a request form so Jorge can confirm.
- If asked about pricing, share our "starting at" prices per service: Cable Management starts at $50, Network Setup starts at $75, Tech and TV Mounting starts at $80, System Setup starts at $40, Cloud or Disk Backup starts at $45, Tech Support starts at $30. Explain that these are starting prices and the final cost depends on the scope of the job. We use flat-rate pricing with no hidden fees and offer free assessments. Always encourage submitting the request form for an exact quote.
- Always encourage submitting the request form on the site for a personalized quote or to book service.
- Do not use em dashes, slang, or emojis. Keep language clear and professional but friendly.
- Keep replies friendly, concise, and under 3 sentences when possible. No upselling, no jargon, no condescension, that's our brand.`;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      // Create chat with context
      const chat = model.startChat({
        history: [
          {
            role: 'user',
            parts: [{ text: systemContext }]
          },
          {
            role: 'model',
            parts: [{ text: 'I understand. I\'m ready to help customers with questions about MTS Tech Solutions services.' }]
          }
        ]
      });

      // Send message and get response
      const result = await chat.sendMessage(userMessage);
      const response = await result.response;
      const botReply = response.text();

      // Add bot response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: botReply 
      }]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I\'m having trouble right now. Please email us at jorge@mtstechsolutions.com or submit a request form.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    'What services do you offer?',
    'Do you service my area?',
    'How much do your services cost?',
    'What are your hours?'
  ];

  const handleQuickQuestion = (question) => {
    setInput(question);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className={`chat-button ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open chat"
      >
        <svg className="chat-button-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span className="chat-button-label">Ask ChatBot!</span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          {/* Header */}
          <div className="chat-header">
            <div>
              <h3>MTS Support</h3>
              <p>Ask us anything</p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="chat-close"
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`message ${message.role === 'user' ? 'message-user' : 'message-assistant'}`}
              >
                <div className="message-content">
                  {message.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message message-assistant">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length === 1 && (
            <div className="quick-questions">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickQuestion(question)}
                  className="quick-question-btn"
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="chat-input-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              className="chat-input"
              disabled={loading}
            />
            <button 
              type="submit" 
              className="chat-send-btn"
              disabled={loading || !input.trim()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
